// ============================================
// VIEWASCLIENT.JSX - Admin "View Client Side" picker
// ============================================
// Lets a system admin pick any company and drop into that company's client
// side as a test member, to verify the client experience end-to-end.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useGetAllCompaniesQuery,
  useEnterClientViewMutation,
} from "../../../services/api";
import { setViewAs } from "../../../store/slices/authSlice";
import "./ViewAsClient.css";

const IconSearch = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconEye = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default function ViewAsClient() {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [enteringId, setEnteringId] = useState(null);
  const [enterError, setEnterError] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [enterClientView] = useEnterClientViewMutation();

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const {
    data: companiesData,
    isLoading,
    error,
  } = useGetAllCompaniesQuery({
    page: 1,
    limit: 50,
    search: debouncedQuery || undefined,
  });

  const companies = companiesData?.companies || [];

  const handleEnter = async (company) => {
    setEnterError(null);
    setEnteringId(company._id);
    try {
      const res = await enterClientView(company._id).unwrap();
      dispatch(setViewAs(res));
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to enter client view:", err);
      setEnterError(
        err?.data?.message || "Failed to enter this company's client view."
      );
      setEnteringId(null);
    }
  };

  return (
    <div className="viewas">
      <div className="viewas-top">
        <div className="viewas-back" onClick={() => navigate("/admin/dashboard")}>
          ← Back to Admin
        </div>
        <h1 className="viewas-title">View Client Side</h1>
        <p className="viewas-subtitle">
          Pick a company to enter its client side as an admin test member. Your
          actions there use a separate test identity and won't affect real
          employees' data.
        </p>
      </div>

      <div className="viewas-filterbar">
        <div className="viewas-count">
          Companies – <strong>{String(companies.length).padStart(2, "0")}</strong>
        </div>
        <div className="viewas-search">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search companies"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {enterError && <div className="viewas-error">{enterError}</div>}

      {isLoading && <div className="viewas-loading">Loading companies...</div>}

      {error && (
        <div className="viewas-error">Failed to load companies. Please try again.</div>
      )}

      {!isLoading && !error && companies.length === 0 && (
        <div className="viewas-empty">No companies found.</div>
      )}

      {!isLoading && !error && companies.length > 0 && (
        <div className="viewas-grid">
          {companies.map((company) => (
            <div className="viewas-card" key={company._id}>
              <div className="viewas-card-head">
                <div className="viewas-logo">
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <div className="viewas-meta">
                  <div className="viewas-name">{company.name}</div>
                  <div className="viewas-sub">
                    {company.memberCount || 0} members
                    <span
                      className={`viewas-status ${company.isActive ? "active" : "inactive"}`}
                    >
                      {company.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              <button
                className="viewas-enter-btn"
                onClick={() => handleEnter(company)}
                disabled={enteringId === company._id}
              >
                {enteringId === company._id ? (
                  "Entering..."
                ) : (
                  <>
                    <IconEye /> Enter client view
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
